import React, { useState, useEffect, useCallback } from 'react';
import { Bot, RefreshCw, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { getAIReferrers, AIReferrerSummaryResponse } from '../../services/api';
import './AIReferrerPanel.css';

interface AIReferrerPanelProps {
    projectId: string;
}

/** Show the URL path (more scannable than the full origin); full URL stays on title attr. */
const displayPage = (page: string): string => {
    try {
        const u = new URL(page);
        return (u.pathname || '/') + (u.search || '');
    } catch {
        return page;
    }
};

const TrendIcon: React.FC<{ trend: 'up' | 'down' | 'flat' }> = ({ trend }) => {
    if (trend === 'up') return <TrendingUp size={14} className="air-trend-up" />;
    if (trend === 'down') return <TrendingDown size={14} className="air-trend-down" />;
    return <Minus size={14} className="air-trend-flat" />;
};

const AIReferrerPanel: React.FC<AIReferrerPanelProps> = ({ projectId }) => {
    const [data, setData] = useState<AIReferrerSummaryResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAIReferrers(projectId, { days: 30, limit: 20 });
            setData(result);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load AI referrer data.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const pages = data?.pages ?? [];
    const summary = data?.summary;
    const days = summary?.params?.days ?? 30;

    return (
        <GlassCard className="air-panel">
            <div className="air-header">
                <div className="air-title">
                    <Bot size={16} />
                    <span>Pages AI Recommends</span>
                </div>
                <div className="air-header-right">
                    {summary && summary.total_ai_visits > 0 && (
                        <span className="air-summary-text">
                            {summary.total_ai_visits.toLocaleString()} AI visits · {summary.pages_cited} pages · {summary.platforms_seen} platforms
                        </span>
                    )}
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={fetchData}
                        disabled={loading}
                        title="Reload AI referrer data"
                        icon={<RefreshCw size={16} className={loading ? 'air-spinning' : ''} />}
                    />
                </div>
            </div>
            <p className="air-subtitle">
                Pages ChatGPT, Perplexity, Gemini, Copilot and Claude are citing and sending visitors to — last {days} days.
            </p>

            {loading && (
                <div className="air-loading">
                    <RefreshCw size={16} className="air-spinning" />
                    <span>Loading AI referrer data…</span>
                </div>
            )}

            {!loading && error && (
                <div className="air-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && data && pages.length === 0 && (
                <div className="air-empty">
                    <Bot size={32} className="air-empty-icon" />
                    <p>No AI referral traffic detected in the last {days} days.</p>
                    <p style={{ fontSize: '0.75rem' }}>
                        As AI assistants start citing your pages, they will appear here. No GA4 needed — powered by AIME first-party tracking.
                    </p>
                </div>
            )}

            {!loading && !error && pages.length > 0 && (
                <table className="seo-keyword-table air-table">
                    <thead>
                        <tr>
                            <th>Page</th>
                            <th>AI Platforms</th>
                            <th style={{ textAlign: 'right' }}>Visits</th>
                            <th style={{ textAlign: 'right' }}>Trend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pages.map(row => (
                            <tr key={row.page}>
                                <td>
                                    <div className="air-page-cell" title={row.page}>
                                        {displayPage(row.page)}
                                    </div>
                                </td>
                                <td>
                                    <div className="air-platform-badges">
                                        {row.platforms.map(p => (
                                            <GlassBadge key={p.platform} variant="info" size="sm">
                                                {p.label}
                                            </GlassBadge>
                                        ))}
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                    {row.total_visits.toLocaleString()}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <TrendIcon trend={row.trend} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </GlassCard>
    );
};

export default AIReferrerPanel;
